import { ApiRequest, SendError } from '@/helpers';
import { Notify, Dialog } from 'quasar';

import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import router from '@/router/router';
import { tabOutlineX } from 'quasar-extras-svg-icons/tabler-icons-v3';
import { getLink } from '@/utils/notifications';
import { Sentry } from '@/vendors/Sentry';

export default {
  namespaced: true,
  state: {
    initialized: false,
    registered: false,
    token: null,
    notification: null,
  },
  actions: {
    async initPushNotifications({ dispatch }) {
      // Create Listeners
      if (Capacitor.isNativePlatform()) {
        // Remove Listeners
        await PushNotifications.removeAllListeners();

        // Registered Listener
        PushNotifications.addListener('registration', async (token) => {
          await dispatch('registered', { token: token.value });
        });

        // Registered Error Listener
        PushNotifications.addListener('registrationError', async () => {
          await dispatch('notRegistered');
        });

        // Push Notification Recieved Listener
        PushNotifications.addListener(
          'pushNotificationReceived',
          async (notification) => {
            await dispatch('receivePush', { notification });
          }
        );

        // Method called when tapping on a notification
        PushNotifications.addListener(
          'pushNotificationActionPerformed',
          async (result) => {
            if (result.actionId === 'tap') {
              await dispatch('tappedPush', {
                notification: result.notification,
              });
            }
          }
        );
      }

      await dispatch('registerPush');
    },
    async checkPermissions() {
      try {
        const result = await PushNotifications.checkPermissions();
        return result.receive;
      } catch (err) {
        Sentry.captureException(err);
        SendError('Unable to check push notification permissions', err);
        return null;
      }
    },
    async registerPush({ dispatch }) {
      const result = await dispatch('checkPermissions');
      if (result && Capacitor.isNativePlatform()) {
        // Check if user has accepted push notifications
        if (result === 'prompt') {
          // Prompt user to accept push notifications
          Dialog.create({
            title: 'Voizor.ai Push Notifications 📣',
            message:
              '\nVoizor.ai uses push notifications to notify users for new recordings, comments, and information. \n\nYou can control what we notify you via your Voizor.ai notification settings.',
            ok: {
              label: 'Continue',
              color: 'black',
              class: 'full-width',
              rounded: true,
            },
            persistent: true,
          }).onOk(async () => {
            const result = await PushNotifications.requestPermissions();
            if (result?.receive === 'granted') {
              // Register with Apple / Google to receive push via APNS/FCM
              await PushNotifications.register();
            } else {
              // Show some error
              await dispatch('notRegistered');
            }
          });
        } else if (result !== 'granted') {
          // If the user has denied in the past
          await dispatch('notRegistered');
        } else {
          // Already approved, but re-register to get new FCM
          await PushNotifications.register();
        }
      } else {
        await dispatch('notRegistered');
      }
    },
    async unregisterPush({ dispatch, state }) {
      if (state.token) {
        await dispatch('deleteFCM', { token: state.token });
      } else {
        // pushChanges('unregistered'); // For logout
      }
    },
    async removeAllDeliveredNotifications({ state }) {
      if (Capacitor.isNativePlatform() && state.registered) {
        await PushNotifications.removeAllDeliveredNotifications(); // Remove badges
      }
    },
    async registered({ commit, dispatch }, data) {
      const token = data.token;

      if (!token) {
        SendError('Missing required token');
        throw new Error('Missing required token');
      }

      commit('setRegistered', true);
      commit('setToken', token);
      await dispatch('registerFCM', { token });

      // We must do this here since it's required to register before we can remove badges
      await dispatch('removeAllDeliveredNotifications');
    },
    async notRegistered({ commit, state }) {
      if (state.initialized) {
        // Didn't happen on initialization
        Notify.create({
          message:
            "Please go to your device's settings and allow push notifications for Voizor.ai.",
          position: 'top',
          actions: [{ icon: tabOutlineX }],
        });
      }
      commit('setInitialized', true);
    },
    receivePush({}, data) {
      const notification = data.notification;

      if (!notification) {
        return reject('Missing required notification');
      }

      // Off for now
      /*
          // Already in conversation, don't show notification
          if (notification.data && notification.data.conversationId
            && window.location.pathname === `/conversation/${notification.data.conversationId}`) {
            return resolve();
          }*/
      /*
          NotificationProgrammatic.open({
            rootClass: 'ac-toast',
            animation: 'notification',
            duration: 3000,
            message: `${notification.title} ${notification.body}`,
            position: 'top',
            variant: 'success',
            queue: true,
            onClose: () => {
              // This will trigger the watcher in App.vue
              commit('setNotification', notification);
            },
          });*/
    },
    tappedPush({}, data) {
      const notification = data.notification;
      router.push(getLink(notification.data));
      if (!notification) {
        throw new Error('Missing required notification');
      }

      // This will trigger the watcher in App.vue
    },
    async registerFCM({ commit, state }, data) {
      const token = data.token;

      if (!token) {
        return reject('Missing required token');
      }
      try {
        await ApiRequest({
          method: 'post',
          url: `${import.meta.env.VITE_API_URL}/fcm-token`,
          data: {
            fcm: token,
          },
        });
      } catch (err) {
        SendError(err);
      }

      if (state.initialized) {
        // Didn't happen on initialization
        /*
            NotificationProgrammatic.open({
              rootClass: 'ac-toast',
              animation: 'notification',
              duration: 3000,
              message: 'Push notifications are now enabled for this device',
              position: 'top',
              variant: 'success',
              // queue: true,
            });*/
      }

      commit('setInitialized', true);
    },
    async deleteFCM({ commit }, data) {
      const token = data.token;

      if (!token) {
        throw new Error('Missing required token');
      }

      await ApiRequest({
        method: 'delete',
        url: `${import.meta.env.VITE_API_URL}/fcm-token`,
        data: {
          fcm: token,
        },
      });

      commit('setToken', null);
      commit('setRegistered', false);
    },
  },
  mutations: {
    setInitialized(state, val) {
      state.initialized = val;
    },
    setRegistered(state, val) {
      state.registered = val;
    },
    setToken(state, val) {
      state.token = val;
    },
    setNotification(state, val) {
      state.notification = val;
    },
  },
  getters: {},
};
